import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import type { PageCursor } from '@application/ports';
import type { PersonId } from '@shared/brand';
import { AUDIT_KIND_COLOR, AUDIT_KIND_GLYPH, escapeCsvField } from '@domain/entities/AuditEntry';
import type { AuditEntry } from '@domain/entities/AuditEntry';
import { AUDIT_KINDS, type AuditKind } from '@domain/vocabulary';
import { CalendarDate } from '@domain/value-objects/CalendarDate';
import { getServices } from '@infrastructure/services';
import { Banner, Chip, EmptyState, Pagination, Spinner, cssVars, cx } from '../../design-system';
import { formatDay } from '../../format';
import { useAreaName } from '../../hooks/useAreaName';
import { useSession } from '../../providers/SessionProvider';
import { useScopedAreaId } from '../../providers/AreaFocusProvider';
import { useToast } from '../../providers/ToastProvider';
import styles from './AuditScreen.module.css';
import { fileNameOf, shortFileName } from './fileLabel';
import { loadFailed } from '../../loadError';
import { DEFAULT_PAGE_SIZE, type PagerControls } from '../../pagination';
import { PageTitle } from '../../shell/PageTitle';

/**
 * Bitácora de movimientos — transcribed from
 * `design/reference/screens/bitacora-de-movimientos.html`.
 *
 * A timeline grouped by day, with filters by kind, actor and date range, plus a
 * CSV export of exactly what is on screen.
 *
 * The export is the part worth reading carefully: audit details are free text
 * written by users, and a CSV field beginning with `=` is executed as a formula
 * by Excel and Sheets. `escapeCsvField` neutralises that, so exporting a log
 * cannot run something on the machine of whoever opens it.
 */

type RangePreset = 'todo' | '7' | '30' | 'mes';

const RANGE_PRESETS: readonly { id: RangePreset; label: string }[] = [
  { id: 'todo', label: 'Todo' },
  { id: '7', label: '7 días' },
  { id: '30', label: '30 días' },
  { id: 'mes', label: 'Este mes' },
];

/**
 * Cuántos movimientos se le piden al servidor en cada tanda.
 *
 * No es el tamaño de la página que se ve —ése lo elige quien mira, y es de
 * diez—: es de cuánto en cuánto se trae la bitácora. Con quince, la primera
 * página y la mitad de la segunda llegan en una sola lectura.
 */
const PAGE_SIZE = 15;

/**
 * How far back to look for the names in the actor selector.
 *
 * Not the whole log: the selector is a convenience, and reading five thousand
 * documents to populate a dropdown is a bill nobody agreed to. Two hundred
 * covers months of movement on an area this size.
 */
const ACTOR_SAMPLE = 200;

/** Read in big pages when exporting; the reader is waiting on a file, not a screen. */
const EXPORT_PAGE = 200;

/** And a ceiling, so «todo el expediente» cannot mean «until the tab dies». */
const EXPORT_CAP = 5000;

export const AuditScreen = (): ReactNode => {
  const session = useSession();
  /* El área que toca mirar: la propia, o la enfocada si su alcance es la
     empresa entera. Ver `AreaFocusProvider`. */
  const areaId = useScopedAreaId(session.areaId);
  const areaName = useAreaName();
  const toast = useToast();

  const [kind, setKind] = useState<AuditKind | 'todos'>('todos');
  const [preset, setPreset] = useState<RangePreset>('todo');
  const [actor, setActor] = useState<PersonId | 'todos'>('todos');
  const [customRangeOpen, setCustomRangeOpen] = useState(false);
  const [customFromInput, setCustomFromInput] = useState('');
  const [customToInput, setCustomToInput] = useState('');
  const [customRange, setCustomRange] = useState<{
    readonly from: CalendarDate;
    readonly to: CalendarDate;
  } | null>(null);

  const today = getServices().clock.today();

  const from = useMemo<CalendarDate | undefined>(() => {
    if (customRange !== null) return customRange.from;
    switch (preset) {
      case 'todo':
        return undefined;
      case '7':
        return today.addDays(-7);
      case '30':
        return today.addDays(-30);
      case 'mes':
        return today.startOfMonth;
    }
  }, [customRange, preset, today]);

  const to = customRange?.to;

  /*
   * The scope, built once.
   *
   * The paged list, the server-side counts and the export all have to ask about
   * exactly the same set of entries. Three copies of this object is three places
   * for a filter to be forgotten, and the symptom would be a summary that
   * disagrees with the list under it.
   */
  const scope = useMemo(
    () => ({
      areaId: areaId,
      ...(kind === 'todos' ? {} : { kind }),
      ...(actor === 'todos' ? {} : { actorPersonId: actor }),
      ...(from === undefined ? {} : { from }),
      ...(to === undefined ? {} : { to }),
    }),
    [areaId, kind, actor, from, to],
  );

  const entries = useInfiniteQuery({
    queryKey: [
      'audit',
      areaId,
      kind,
      preset,
      actor,
      customRange?.from.toISO(),
      customRange?.to.toISO(),
    ],
    /*
     * Fifteen at a time, fetched as the reader scrolls.
     *
     * The log is the one collection here that only grows: at a hundred entries a
     * page it was a single read that got slower every week, and the reader saw
     * the first six. A page the size of a screenful, with the next one requested
     * when the bottom comes into view, keeps the first paint constant no matter
     * how much history there is.
     */
    initialPageParam: undefined as PageCursor | undefined,
    queryFn: async ({ pageParam }) => {
      const result = await getServices().audit.list(scope, {
        limit: PAGE_SIZE,
        ...(pageParam === undefined ? {} : { cursor: pageParam }),
      });
      if (!result.ok) throw loadFailed('No pudimos cargar la bitácora.', result.error);
      return result.value;
    },
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });

  const list = useMemo(
    () => (entries.data?.pages ?? []).flatMap((page) => page.items),
    [entries.data],
  );

  /*
   * The figures in the summary, counted by the server.
   *
   * They used to be `list.filter(...).length` — the loaded pages — which was
   * merely optimistic when a page was a hundred entries and became a lie at
   * fifteen: «Eventos registrados 15» under a log with two hundred. A count is a
   * claim about the record, so it is asked of the record, with
   * `getCountFromServer` rather than by downloading the documents to measure
   * them.
   */
  const totals = useQuery({
    queryKey: ['audit', 'totals', scope],
    queryFn: async () => {
      const audit = getServices().audit;
      const [all, politica, liderazgo] = await Promise.all([
        audit.count(scope),
        audit.count({ ...scope, kind: 'Política' }),
        audit.count({ ...scope, kind: 'Liderazgo' }),
      ]);
      return {
        all: all.ok ? all.value : null,
        politica: politica.ok ? politica.value : null,
        liderazgo: liderazgo.ok ? liderazgo.value : null,
      };
    },
  });

  const { fetchNextPage, hasNextPage, isFetchingNextPage } = entries;

  /*
   * Páginas, y no un scroll que no se acaba.
   *
   * La bitácora se leía dentro de un recuadro con su propia barra, a la altura
   * de la columna de al lado, y pedía la tanda siguiente al acercarse al final.
   * Eran dos scroll anidados —el de la página y el del recuadro— y una lista sin
   * fondo: para llegar al movimiento de hace un mes había que arrastrar hasta
   * él. Desde el 7 oct 2026 va de diez en diez, con «Anterior» y «Siguiente»
   * como los demás listados, y el recuadro mide lo que su página.
   *
   * Es el único listado que **no** tiene su lista entera en memoria: sólo crece
   * y se pide por tandas. Por eso no usa `usePagination`: la página que se ve se
   * corta de lo ya traído, y si no alcanza se pide lo que falte.
   */
  const [pageState, setPageState] = useState({ page: 1, key: '' });
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);

  /* Lo que se está filtrando. Un filtro nuevo es otra lista y empieza por el
     principio; ver `usePagination`, que hace lo mismo. */
  const filterKey = [
    areaId,
    kind,
    preset,
    actor,
    customRange?.from.toISO() ?? '',
    customRange?.to.toISO() ?? '',
  ].join('|');
  if (pageState.key !== filterKey) setPageState({ page: 1, key: filterKey });
  const page = pageState.key === filterKey ? pageState.page : 1;

  const pageStart = (page - 1) * pageSize;
  const shown = useMemo(
    () => list.slice(pageStart, pageStart + pageSize),
    [list, pageStart, pageSize],
  );

  /* La página pide lo que le falta. Una tanda cada vez: al llegar, `list` crece
     y esto vuelve a mirar si ya alcanza. */
  const missing = list.length < pageStart + pageSize;
  useEffect(() => {
    if (missing && hasNextPage && !isFetchingNextPage) void fetchNextPage();
  }, [missing, hasNextPage, isFetchingNextPage, fetchNextPage]);

  /*
   * Cuántos hay, para el pie.
   *
   * Lo dice el servidor, con la misma cuenta que alimenta el resumen de al lado:
   * lo ya traído son quince o treinta, y «1–10 de 15» bajo una bitácora de
   * doscientos sería la misma mentira que esa cuenta vino a quitar. Con todo ya
   * en memoria manda lo que hay, que es más cierto que cualquier cuenta. Y si el
   * servidor no pudo contar, el pie lo dice —«más de»— y deja seguir mientras
   * haya otra tanda.
   */
  const counted = totals.data?.all ?? null;
  const allLoaded = !hasNextPage && !entries.isLoading;
  const total = allLoaded ? list.length : (counted ?? list.length);
  const open = !allLoaded && counted === null;
  const pager: PagerControls = {
    page,
    pageSize,
    pageCount: open ? page + 1 : Math.max(1, Math.ceil(total / pageSize)),
    from: total === 0 ? 0 : pageStart + 1,
    to: open ? pageStart + shown.length : Math.min(pageStart + pageSize, total),
    total,
    open,
    needed: total > DEFAULT_PAGE_SIZE || hasNextPage,
    goTo: (next) => {
      setPageState({ page: next, key: filterKey });
    },
    setPageSize: (size) => {
      setPageSize(size);
      setPageState({ page: 1, key: filterKey });
    },
  };

  /** The export walks the whole expediente, so it can take a moment; say so. */
  const [exporting, setExporting] = useState(false);

  /**
   * Distinct actors with their counts, for the actor selector.
   *
   * Grouped by person id, not by the name written on the entry: `actorName` is a
   * display copy taken at write time, so the same person can appear under several
   * spellings ("Renato Vidal (RR.HH.)", "RR.HH.") and grouping by it would list
   * them twice — and, since the option is keyed by the id, collide. Entries with
   * no actor are system writes and group under their own label.
   */
  /*
   * The selector's options come from a wider read than the page on screen.
   *
   * Firestore has no `DISTINCT`, so the list of actors has to be derived from
   * entries — and deriving it from the fifteen currently loaded meant the filter
   * could only offer whoever happened to be at the top of the log. One bounded
   * read of the same scope, and the names come from the whole recent record
   * instead of from the first screenful.
   */
  const actorSample = useQuery({
    queryKey: ['audit', 'actors', scope],
    queryFn: async () => {
      const result = await getServices().audit.list(scope, { limit: ACTOR_SAMPLE });
      return result.ok ? result.value.items : [];
    },
  });

  const actors = useMemo(() => {
    const counts = new Map<string, { id: PersonId | null; name: string; count: number }>();
    for (const entry of actorSample.data ?? []) {
      const key = entry.actorPersonId ?? `sistema:${entry.actorName}`;
      const current = counts.get(key);
      counts.set(key, {
        id: entry.actorPersonId,
        // Keep the first spelling seen, so the label stays stable as pages load.
        name: current?.name ?? entry.actorName,
        count: (current?.count ?? 0) + 1,
      });
    }
    return [...counts.values()].sort((a, b) => b.count - a.count);
  }, [actorSample.data]);

  /** La página que se ve, agrupada por día y de lo más nuevo a lo más viejo. */
  const grouped = useMemo(() => {
    const byDay = new Map<string, AuditEntry[]>();
    for (const entry of shown) {
      const day = CalendarDate.fromInstantLocal(entry.createdAt).toISO();
      byDay.set(day, [...(byDay.get(day) ?? []), entry]);
    }
    return [...byDay.entries()].sort((a, b) => b[0].localeCompare(a[0]));
  }, [shown]);

  /** Entries that carry an archived document, for the sidebar. */
  /*
   * The eight most recent documents, taken from the same wider read the actor
   * selector uses rather than from the page on screen — otherwise the panel
   * emptied out whenever the first fifteen entries happened to carry no file,
   * and «Sin documentos en este rango» was a statement about the scroll
   * position.
   */
  const documents = useMemo(
    () => (actorSample.data ?? []).filter((entry) => entry.documentPath !== null).slice(0, 8),
    [actorSample.data],
  );

  /**
   * The expediente summary, from the prototype's `histStats`.
   *
   * The last row is a constant on purpose: retention is a policy of the record,
   * not a measurement of it, and someone auditing this log needs to know how
   * long it will still be here.
   */
  const stats: readonly { label: string; value: string | number }[] = [
    { label: 'Alcance', value: session.role === 'super' ? 'Todas las áreas' : areaName },
    { label: 'Eventos registrados', value: totals.data?.all ?? '—' },
    { label: 'Cambios de política', value: totals.data?.politica ?? '—' },
    { label: 'Cambios de liderazgo', value: totals.data?.liderazgo ?? '—' },
    { label: 'Retención del expediente', value: '5 años' },
  ];

  const timeOf = (entry: AuditEntry): string =>
    entry.createdAt.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });

  /*
   * The export asks the database, it does not dump what is on screen.
   *
   * Before, it wrote whatever pages had been scrolled into memory — fifteen rows
   * from a log of two hundred, in a file named `bitacora-2026-08-11.csv` that
   * somebody would open believing it was the record. A partial export that
   * announces itself as complete is the one failure here with consequences
   * outside the screen.
   */
  const exportCsv = async (): Promise<void> => {
    const audit = getServices().audit;
    const all: AuditEntry[] = [];
    let cursor: PageCursor | undefined;

    // Bounded, because «export everything» on a log that only grows has to have
    // an end: at the cap the file is still written and the toast says what it
    // holds, rather than the browser dying halfway through.
    while (all.length < EXPORT_CAP) {
      const page = await audit.list(scope, {
        limit: EXPORT_PAGE,
        ...(cursor === undefined ? {} : { cursor }),
      });
      if (!page.ok) {
        toast.bad('No pudimos leer el expediente completo para exportarlo.');
        return;
      }
      all.push(...page.value.items);
      if (page.value.nextCursor === null) break;
      cursor = page.value.nextCursor;
    }

    exportRows(all);
  };

  const exportRows = (entriesToWrite: readonly AuditEntry[]): void => {
    const header = ['Fecha', 'Hora', 'Tipo', 'Actor', 'Acción', 'Detalle', 'Documento'];

    const rows = entriesToWrite.map((entry) => {
      const date = CalendarDate.fromInstantLocal(entry.createdAt);
      return [
        date.toISO(),
        timeOf(entry),
        entry.kind,
        entry.actorName,
        entry.action,
        entry.detail,
        entry.documentPath ?? '',
      ].map(escapeCsvField);
    });

    const csv = [header.map(escapeCsvField), ...rows].map((row) => row.join(',')).join('\r\n');

    // \uFEFF is the UTF-8 BOM. Without it Excel opens the file as Latin-1 and every
    // accented word arrives mangled — "Aprobación" becomes "AprobaciÃ³n".
    const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);

    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `bitacora-${today.toISO()}.csv`;
    anchor.click();
    URL.revokeObjectURL(url);

    toast.ok(
      entriesToWrite.length >= EXPORT_CAP
        ? `Exportamos los primeros ${String(entriesToWrite.length)} movimientos. Acota el rango para el resto.`
        : `Exportamos ${String(entriesToWrite.length)} movimientos.`,
    );
  };

  const rangeNote =
    from === undefined
      ? 'Sin filtros: todo el expediente.'
      : `Desde el ${formatDay(from)} hasta ${to === undefined ? 'hoy' : formatDay(to)}.`;

  return (
    <div className="flow">
      <header className={styles.header}>
        <div>
          <PageTitle className={styles.title}>Bitácora de movimientos</PageTitle>
          <p className={styles.note}>
            Solicitudes, decisiones, documentos y cambios de política. Solo movimientos del área{' '}
            {areaName}.
          </p>
        </div>
      </header>

      {/*
        * Every filter in one bar, sitting on top of the log it filters.
        *
        * They were spread across the page header — the date presets under the
        * title, the kinds and the actor floated off to the right — and once the
        * counts came off the chips the row reflowed up beside the heading, where
        * they read as decoration belonging to the page rather than controls
        * belonging to the table. Grouped and docked here, the relationship needs
        * no explaining.
        */}
      <section className={styles.filters} aria-label="Filtros de la bitácora">
        <div className={styles.filterRow}>
          <div className={styles.rangeRow}>
            {RANGE_PRESETS.map((option) => (
              <button
                key={option.id}
                type="button"
                aria-pressed={preset === option.id}
                className={cx(styles.rangeChip, preset === option.id && styles.rangeChipActive)}
                onClick={() => {
                  setPreset(option.id);
                  setCustomRange(null);
                  setCustomRangeOpen(false);
                }}
              >
                {option.label}
              </button>
            ))}
            <button
              type="button"
              className={cx(
                styles.rangeChip,
                styles.customRangeChip,
                customRange !== null && styles.rangeChipActive,
              )}
              onClick={() => {
                setCustomFromInput(customRange?.from.toISO() ?? today.addDays(-30).toISO());
                setCustomToInput(customRange?.to.toISO() ?? today.toISO());
                setCustomRangeOpen((open) => !open);
              }}
            >
              <span aria-hidden="true">🗓</span> Elegir rango
            </button>
            {customRangeOpen && (
              <div className={styles.rangePopup}>
                <span className={styles.rangePopupTitle}>Rango de fechas</span>
                <label className={styles.rangeField}>
                  Desde
                  <input
                    type="date"
                    value={customFromInput}
                    onChange={(event) => {
                      setCustomFromInput(event.target.value);
                    }}
                  />
                </label>
                <label className={styles.rangeField}>
                  Hasta
                  <input
                    type="date"
                    value={customToInput}
                    onChange={(event) => {
                      setCustomToInput(event.target.value);
                    }}
                  />
                </label>
                <button
                  type="button"
                  className={styles.rangeApply}
                  onClick={() => {
                    const parsedFrom = CalendarDate.fromISO(customFromInput);
                    const parsedTo = CalendarDate.fromISO(customToInput);
                    if (
                      !parsedFrom.ok ||
                      !parsedTo.ok ||
                      parsedFrom.value.compare(parsedTo.value) > 0
                    ) {
                      toast.warn('El rango de fechas no es válido.');
                      return;
                    }
                    setCustomRange({ from: parsedFrom.value, to: parsedTo.value });
                    setPreset('todo');
                    setCustomRangeOpen(false);
                  }}
                >
                  Aplicar rango
                </button>
              </div>
            )}
          </div>

          <div className={styles.rangeNote}>{rangeNote}</div>
        </div>

        {/*
          * No counts on these chips, and that is the point.
          *
          * They used to read `Documento 0` — the number of documents *in the
          * fifteen entries currently loaded*, not in the log. A chip that says
          * zero over a filter that would return forty is worse than a chip with
          * no number at all: it stops the reader from clicking. Each chip
          * re-queries the database; the count of everything lives in the summary
          * beside it, where it is asked of the server.
          */}
        <div className={styles.controls}>
          <div className={styles.kindTrack} role="group" aria-label="Filtrar por tipo">
            <button
              type="button"
              aria-pressed={kind === 'todos'}
              className={cx(styles.kindChip, kind === 'todos' && styles.kindChipActive)}
              onClick={() => {
                setKind('todos');
              }}
            >
              Todos
            </button>
            {AUDIT_KINDS.filter((k) => k !== 'Desconocido').map((option) => (
              <button
                key={option}
                type="button"
                aria-pressed={kind === option}
                className={cx(styles.kindChip, kind === option && styles.kindChipActive)}
                style={cssVars({ '--kind-color': AUDIT_KIND_COLOR[option] })}
                onClick={() => {
                  setKind(option);
                }}
              >
                {option}
              </button>
            ))}
          </div>

          <div className={styles.actorRow}>
            <label className={styles.actorLabel}>
              <span>Actor</span>
              <select
                className={styles.actorSelect}
                value={actor}
                onChange={(event) => {
                  setActor(
                    event.target.value === 'todos' ? 'todos' : (event.target.value as PersonId),
                  );
                }}
              >
                <option value="todos">Todos los actores</option>
                {actors.map((entry) =>
                  entry.id === null ? null : (
                    <option key={entry.id} value={entry.id}>
                      {entry.name} ({entry.count})
                    </option>
                  ),
                )}
              </select>
            </label>

            <button
              type="button"
              className={styles.exportButton}
              disabled={list.length === 0 || exporting}
              onClick={() => {
                setExporting(true);
                void exportCsv().finally(() => {
                  setExporting(false);
                });
              }}
            >
              <span className={styles.exportGlyph} aria-hidden="true">
                ↓
              </span>
              {exporting ? 'Exportando…' : 'Exportar CSV'}
            </button>
          </div>
        </div>
      </section>

      <div className={styles.layout}>
        <section className={cx(styles.panel, styles.logPanel)}>
          <div className={styles.panelHead}>
            <h2 className={styles.panelTitle}>Bitácora</h2>
            <span className={styles.panelMeta}>
              {/* Los que hay, no los que ya se trajeron. Cuando el servidor no
                  pudo contarlos se dice lo que se sabe: cuántos van y que hay más. */}
              {counted ?? list.length} eventos{counted === null && hasNextPage ? ' · hay más' : ''}
            </span>
          </div>

          {entries.isLoading || (shown.length === 0 && missing && hasNextPage) ? (
            <Spinner label="Cargando la bitácora" />
          ) : entries.error !== null ? (
            <Banner tone="danger">No pudimos cargar la bitácora.</Banner>
          ) : grouped.length === 0 ? (
            <EmptyState
              glyph="§"
              title="Sin movimientos"
              note="Ajusta los filtros o amplía el rango de fechas."
            />
          ) : (
            grouped.map(([day, dayEntries]) => (
              <div key={day} className={styles.dayGroup}>
                <div className={styles.dayHead}>
                  <h3 className={styles.dayLabel}>
                    {formatDay(CalendarDate.fromISOOrThrow(day))}
                  </h3>
                  <span className={styles.dayRule} aria-hidden="true" />
                </div>

                <ul className={styles.entries}>
                  {dayEntries.map((entry) => (
                    <li key={entry.id} className={styles.entry}>
                      <span
                        className={styles.entryGlyph}
                        style={cssVars({ '--kind-color': AUDIT_KIND_COLOR[entry.kind] })}
                        aria-hidden="true"
                      >
                        {AUDIT_KIND_GLYPH[entry.kind]}
                      </span>

                      <div className={styles.entryBody}>
                        <div className={styles.entryAction}>{entry.action}</div>
                        {entry.detail.length > 0 && (
                          <div className={styles.entryDetail}>{entry.detail}</div>
                        )}
                        <div className={styles.entryTags}>
                          <Chip
                            background={`color-mix(in srgb, ${AUDIT_KIND_COLOR[entry.kind]} 14%, transparent)`}
                            foreground={AUDIT_KIND_COLOR[entry.kind]}
                          >
                            {entry.kind}
                          </Chip>
                          <span className={styles.entryActor}>
                            {entry.actorName} · {timeOf(entry)}
                          </span>
                        </div>
                      </div>

                      {entry.documentPath !== null && (
                        <button
                          type="button"
                          className={styles.entryDoc}
                          /* El nombre entero, donde se puede leer sin costarle
                             ancho a la descripción del movimiento. */
                          title={fileNameOf(entry.documentPath)}
                          aria-label={`Descargar ${fileNameOf(entry.documentPath)}`}
                          onClick={() => {
                            toast.info('El documento se sirve con una URL firmada de Storage.');
                          }}
                        >
                          ↓ {shortFileName(fileNameOf(entry.documentPath))}
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))
          )}

          <Pagination pager={pager} noun="movimientos" />
        </section>

        <div className={styles.aside}>
          <section className={cx(styles.panel, styles.panelSmall)}>
            <div className={styles.panelHead}>
              <h2 className={styles.panelTitle}>Resumen del expediente</h2>
            </div>
            <div className={styles.statList}>
              {stats.map((stat) => (
                <div key={stat.label} className={styles.statRow}>
                  <span className={styles.statLabel}>{stat.label}</span>
                  <strong className={styles.statValue}>{stat.value}</strong>
                </div>
              ))}
            </div>
          </section>

          <section className={cx(styles.panel, styles.panelSmall)}>
            <div className={styles.panelHead}>
              <h2 className={styles.panelTitle}>Documentos archivados</h2>
              <span className={styles.panelMeta}>{today.year}</span>
            </div>

            {documents.length === 0 ? (
              <EmptyState glyph="▤" title="Sin documentos en este rango" />
            ) : (
              <ul className={styles.docs}>
                {documents.map((entry) => (
                  <li key={entry.id} className={styles.docRow}>
                    <span className={styles.docBadge} aria-hidden="true">
                      PDF
                    </span>
                    <div className={styles.docText}>
                      {/* Recortado por el medio y no con `text-overflow`: lo que
                          la elipsis del CSS se lleva es la extensión, que es lo
                          primero que se quiere saber de un adjunto. El nombre
                          entero está en el `title`. Ver `shortFileName`. */}
                      <div
                        className={styles.docName}
                        title={fileNameOf(entry.documentPath ?? '')}
                      >
                        {shortFileName(fileNameOf(entry.documentPath ?? ''))}
                      </div>
                      <div className={styles.docMeta}>
                        {entry.actorName} · {formatDay(CalendarDate.fromInstantLocal(entry.createdAt))}
                      </div>
                    </div>
                    <button
                      type="button"
                      className={styles.docDownload}
                      aria-label={`Descargar ${fileNameOf(entry.documentPath ?? '')}`}
                      onClick={() => {
                        toast.info('El documento se sirve con una URL firmada de Storage.');
                      }}
                    >
                      ↓
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      </div>
    </div>
  );
};

